import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { useAuthContext } from "@/lib/auth-context";
import { jsonFetch } from "@/utils/jsonFetch";
import LoadingSpinner from "../../../components/LoadingSpinner";

const CORE_API = import.meta.env.VITE_CORE_API_URL || "";

type GuardianThreadSummary = {
  thread_id: string;
  ticket_id: string;
  student_id: string;
  student_name: string;
  teacher_name: string;
  last_message_at: string;
  last_message_excerpt: string;
  unread_count: number;
};

export default function GuardianMessagesInbox() {
  const { user, session } = useAuthContext();
  const [threads, setThreads] = useState<GuardianThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compose state
  const [composing, setComposing] = useState(false);
  const [students, setStudents] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const loadThreads = async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await jsonFetch(`${CORE_API}/api/v1/messages/threads`) as any;
      const data = raw?.data ?? raw;
      setThreads(data?.threads || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load your messages right now.");
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const raw = await jsonFetch(`${CORE_API}/api/v1/parent/home`) as any;
      const studentList = (raw?.students || []).map((s: any) => ({
        id: s.id,
        name: s.display_name || s.displayName || s.full_name || s.fullName || 'Student',
      }));
      setStudents(studentList);
      if (studentList.length === 1) setSelectedStudentId(studentList[0].id);
    } catch { /* ignore */ }
  };

  const handleSend = async () => {
    if (!selectedStudentId || !composeBody.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      await jsonFetch(`${CORE_API}/api/v1/messages/send`, {
        method: "POST",
        body: JSON.stringify({
          student_id: selectedStudentId,
          body: composeBody.trim(),
        }),
      });
      setComposing(false);
      setComposeBody("");
      setSelectedStudentId("");
      loadThreads();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (session?.access_token && user?.id) {
      loadThreads();
    }
  }, [session?.access_token, user?.id]);

  if (loading) {
    return (
      <div className="w-full flex justify-center mt-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-4 text-center">
        <h1 className="text-2xl font-bold">Messages</h1>
        <p className="text-red-600 mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-7 w-7 text-blue-600" />
          Messages
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setComposing(!composing); if (!composing) loadStudents(); }}
            className="flex items-center gap-1 rounded-lg bg-brick px-3 py-1.5 text-sm font-semibold text-white hover:bg-brickDark"
          >
            <PlusCircleIcon className="h-4 w-4" />
            New Message
          </button>
          <Link to="/portal/parent" className="text-sm text-blue-600 hover:text-blue-500">
            Back to dashboard
          </Link>
        </div>
      </div>

      {/* Compose new message */}
      {composing && (
        <div className="mb-6 rounded-xl border border-sand bg-white p-4 space-y-3 shadow-sm">
          <h3 className="text-sm font-semibold text-cocoa">Send a message to your child's teacher</h3>
          {students.length > 1 && (
            <select
              className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              <option value="">Select student...</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
          {students.length === 1 && (
            <p className="text-sm text-cocoa/70">About: {students[0].name}</p>
          )}
          <textarea
            className="w-full rounded-lg border border-sand px-3 py-2 text-sm"
            rows={3}
            placeholder="Type your message..."
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
          />
          {sendError && <p className="text-xs text-red-600">{sendError}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setComposing(false)}
              className="rounded-lg border border-sand px-3 py-1.5 text-sm text-cocoa/70 hover:bg-sand/20"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !selectedStudentId || !composeBody.trim()}
              className="rounded-lg bg-brick px-4 py-1.5 text-sm font-semibold text-white hover:bg-brickDark disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      )}

      {threads.length === 0 && !composing ? (
        <div className="mt-8 rounded-xl border border-dashed border-gray-300 bg-white p-6 text-center">
          <ChatBubbleLeftRightIcon className="h-8 w-8 mx-auto text-gray-400" />
          <p className="mt-3 text-sm text-gray-600">
            No conversations yet. Click <strong>New Message</strong> to start a conversation with your child's teacher.
          </p>
        </div>
      ) : (
        <div className="mt-4 bg-white rounded-xl shadow-sm divide-y divide-gray-100">
          {threads.map((thread) => (
            <Link
              key={thread.thread_id}
              to={`/portal/guardian/messages/thread/${thread.thread_id}`}
              className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition"
            >
              <div className="mt-1">
                <UserCircleIcon className="h-9 w-9 text-gray-400" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {thread.teacher_name || "Teacher"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      for {thread.student_name || "Student"}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 whitespace-nowrap">
                    {thread.last_message_at
                      ? new Date(thread.last_message_at).toLocaleString()
                      : ""}
                  </p>
                </div>

                <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                  {thread.last_message_excerpt || "No messages yet."}
                </p>
              </div>

              {thread.unread_count > 0 && (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
                  {thread.unread_count}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
